import { Button, Callout, Card } from "@obsina/design-system";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { AuthenticatedAccount } from "../App";
import { AppShell } from "../AppShell";
import { enMessages } from "../messages";
import type { AppMessages } from "../messages";
import type { AppView } from "../navigation";
import { consentMessages } from "./messages";
import { createConsentApi } from "./consent-api";
import type { ConsentApi, ConsentContext, ConsentFailure } from "./consent-api";
import { clearConsentRequest, readConsentRequest } from "./consent-request";
import { createAccessTokenReader } from "./session-token";
import type { AccessTokenReader } from "./session-token";
import { readMcpEndpoint } from "./mcp-endpoint";
import type { McpEndpoint } from "./mcp-endpoint";
import "./integrations.css";

export interface ConnectAssistantViewProps {
  account?: AuthenticatedAccount | null;
  messages?: AppMessages;
  activeView?: AppView;
  onNavigate?: (view: AppView) => void;
  onSignOut?: () => Promise<void> | void;
  /** Deployment configuration override; production reads `import.meta.env`. */
  endpoint?: McpEndpoint;
  /** The pending request; injected by the tests, read from the tab otherwise. */
  consentRequest?: string | null;
  /** Seams for the tests. Production builds both from configuration. */
  api?: ConsentApi;
  readAccessToken?: AccessTokenReader;
  /** Where an approved or declined connection sends the browser. */
  navigate?: (url: string) => void;
}

type Phase =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly context: ConsentContext }
  | {
      readonly kind: "deciding";
      readonly context: ConsentContext;
      readonly decision: "approve" | "deny";
    }
  | { readonly kind: "leaving" }
  | {
      readonly kind: "failed";
      readonly reason: ConsentFailure | "unpublished";
    };

function defaultNavigate(url: string): void {
  window.location.assign(url);
}

/**
 * The consent screen an assistant's authorization lands on.
 *
 * This is the leg the MCP server cannot perform itself: it has no way to learn
 * who is at the keyboard, because a browser navigation carries no
 * `Authorization` header and the API's session cookie is bound to the API
 * origin. Here the session already exists, so the person is asked plainly, and
 * their own token is what carries the answer back.
 *
 * The screen states one decision. The assistant's name is in the heading and
 * nowhere else; read-only is said once; the organisation and the two
 * capabilities are the only other facts on it. Nothing is approved by arriving
 * — a token is minted only after the person presses Connect.
 */
export function ConnectAssistantView({
  account = null,
  messages = enMessages,
  activeView = "connect_assistant",
  onNavigate,
  onSignOut,
  endpoint,
  consentRequest,
  api,
  readAccessToken,
  navigate = defaultNavigate,
}: ConnectAssistantViewProps) {
  const copy = consentMessages;
  const headingId = useId();
  const destinationId = useId();
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  // Resolved once per mount: the request is a property of this tab's flow, and
  // re-reading it mid-decision would let a second tab change what is displayed
  // while somebody is looking at it.
  const requestRef = useRef<string | null>(
    consentRequest === undefined ? readConsentRequest() : consentRequest,
  );
  const request = requestRef.current;

  const resolved = endpoint ?? readMcpEndpoint(import.meta.env);
  const clientRef = useRef<{
    api: ConsentApi | null;
    token: AccessTokenReader;
  } | null>(null);
  clientRef.current ??= {
    api: api ?? (resolved.published ? createConsentApi(resolved.url) : null),
    token: readAccessToken ?? createAccessTokenReader(),
  };
  const client = clientRef.current;

  useEffect(() => {
    let active = true;
    if (request === null) {
      setPhase({ kind: "idle" });
      return;
    }
    const api = client.api;
    if (!api) {
      setPhase({ kind: "failed", reason: "unpublished" });
      return;
    }
    const load = async (): Promise<void> => {
      const token = await client.token();
      if (!active) return;
      if (!token) {
        setPhase({ kind: "failed", reason: "sign_in_required" });
        return;
      }
      const result = await api.context(request, token);
      if (!active) return;
      setPhase(
        result.kind === "ok"
          ? { kind: "ready", context: result.context }
          : { kind: "failed", reason: result.reason },
      );
    };
    setPhase({ kind: "loading" });
    void load();
    return () => {
      active = false;
    };
  }, [client, request]);

  const decide = useCallback(
    async (decision: "approve" | "deny"): Promise<void> => {
      const api = client.api;
      if (phase.kind !== "ready" || request === null || !api) return;
      setPhase({ kind: "deciding", context: phase.context, decision });
      const token = await client.token();
      if (!token) {
        setPhase({ kind: "failed", reason: "sign_in_required" });
        return;
      }
      const result = await api.decide(request, decision, token);
      if (result.kind === "failed") {
        setPhase({ kind: "failed", reason: result.reason });
        return;
      }
      // The flow is over for this tab either way — approved or declined — so the
      // stored request goes before the browser leaves. A back button must not
      // land on a screen that offers to approve something already decided.
      clearConsentRequest();
      setPhase({ kind: "leaving" });
      navigate(result.url);
    },
    [client, navigate, phase, request],
  );

  const signOut = onSignOut
    ? () => {
        void onSignOut();
      }
    : undefined;

  const deciding = phase.kind === "deciding" ? phase.decision : null;
  const busy = phase.kind === "deciding" || phase.kind === "leaving";

  return (
    <AppShell
      account={account}
      activeView={activeView}
      messages={messages}
      onNavigate={onNavigate}
      onSignOut={signOut}
    >
      <section className="page-stack mcp-consent-page">
        {phase.kind === "loading" && (
          <p className="mcp-consent__status" role="status">
            {copy.loading}
          </p>
        )}

        {phase.kind === "idle" && (
          <Callout title={copy.idle.title} tone="info">
            {copy.idle.body}
          </Callout>
        )}

        {phase.kind === "failed" && (
          <Callout announce title={copy.failure.title} tone="danger">
            {copy.failure[phase.reason]}
          </Callout>
        )}

        {(phase.kind === "ready" ||
          phase.kind === "deciding" ||
          phase.kind === "leaving") && (
          <Card
            aria-labelledby={headingId}
            as="section"
            className="mcp-consent"
          >
            {phase.kind === "leaving" ? (
              <p className="mcp-consent__status" role="status">
                {copy.leaving}
              </p>
            ) : (
              <>
                <h1 className="mcp-consent__heading" id={headingId}>
                  {copy.headingFor(phase.context.clientName)}
                </h1>
                <p className="mcp-consent__lede">{copy.lede}</p>

                <dl className="mcp-consent__facts">
                  <dt>{copy.organisationLabel}</dt>
                  <dd>
                    {phase.context.tenantName ?? copy.organisationUnknown}
                  </dd>
                  <dt>{copy.roleLabel}</dt>
                  <dd>
                    {phase.context.role === "owner"
                      ? copy.roleOwner
                      : copy.roleMember}
                  </dd>
                </dl>

                <h2 className="mcp-consent__subheading">{copy.allowsLabel}</h2>
                <ul className="mcp-consent__scopes">
                  {phase.context.scopes.map((scope) => (
                    <li key={scope}>{copy.scopes[scope] ?? scope}</li>
                  ))}
                </ul>

                {/* The destination belongs to the decision, so it shares the
                    decision's row instead of becoming a band of its own, and
                    the approve button carries it as its description — a screen
                    reader hears where the grant goes before it is granted. */}
                <div className="mcp-consent__decide">
                  <div className="mcp-consent__actions">
                    <Button
                      aria-describedby={destinationId}
                      disabled={busy}
                      loading={deciding === "approve"}
                      loadingLabel={copy.approving}
                      onClick={() => void decide("approve")}
                      type="button"
                    >
                      {copy.approve}
                    </Button>
                    <Button
                      disabled={busy}
                      loading={deciding === "deny"}
                      loadingLabel={copy.denying}
                      onClick={() => void decide("deny")}
                      type="button"
                      variant="secondary"
                    >
                      {copy.deny}
                    </Button>
                  </div>
                  <p className="mcp-consent__destination" id={destinationId}>
                    {copy.destinationLabel}{" "}
                    <strong className="mcp-consent__destination-origin">
                      {phase.context.redirectOrigin}
                    </strong>
                  </p>
                </div>
              </>
            )}
          </Card>
        )}
      </section>
    </AppShell>
  );
}
